import { Icon } from "@iconify/react";
import { AppModal, Input, Textarea } from "@/shared/components/dialog";
import { EmptyState } from "@/shared/components/EmptyState";
import { Button } from "@/shared/components/button";
import { Select } from "@/shared/components/select";
import { Segmented, Checkbox, Skeleton } from "antd";
import type { PatientReportedConcern } from "@/shared/types";
import type { HealthConcernRecord } from "@/features/patient/clinical-history/utils/adaptClinicalHistory";
import { NotAvailable } from "@/shared/components/NotAvailable";
import { cn } from "@/shared/lib/utils";
import { useState, useEffect } from "react";

type HealthConcern = HealthConcernRecord;

interface HealthConcernsSectionProps {
  concerns: HealthConcernRecord[];
  patientConcerns: PatientReportedConcern[];
  onAddPatientConcern?: (concern: Omit<PatientReportedConcern, "id" | "reportedDate" | "status">) => void;
  isLoading?: boolean;
}

const categoryConfig: Record<string, { icon: string; label: string }> = {
  symptom:       { icon: "lucide:activity",  label: "Symptom"       },
  condition:     { icon: "lucide:heart",     label: "Condition"     },
  preventive:    { icon: "lucide:shield",    label: "Preventive"    },
  lifestyle:     { icon: "lucide:leaf",      label: "Lifestyle"     },
  mental_health: { icon: "lucide:brain",     label: "Mental Health" },
};

const severityConfig: Record<string, { label: string; bg: string; text: string }> = {
  mild:     { label: "Mild",     bg: "bg-[#E4002B]/50", text: "!text-white" },
  moderate: { label: "Moderate", bg: "bg-[#E4002B]/50", text: "!text-white" },
  severe:   { label: "Severe",   bg: "bg-[#E4002B]/50", text: "!text-white" },
  new:      { label: "New",      bg: "bg-[#E4002B]/50", text: "!text-white" },
  unknown:  { label: "Severity not recorded", bg: "bg-[#F3F4F6]", text: "!text-[#6B7280]" },
};

const urgencyConfig: Record<string, { label: string; bg: string; text: string }> = {
  routine: { label: "Routine", bg: "bg-[#E4002B]/50", text: "!text-white" },
  soon:    { label: "Soon",    bg: "bg-[#E4002B]/50", text: "!text-white" },
  urgent:  { label: "Urgent",  bg: "bg-[#E4002B]/50", text: "!text-white" },
};

function formatDate(dateString: string): string {
  if (!dateString) return "Not available";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

// ── Concern list card — matches image exactly ──────────────────────────────
function ConcernCard({
  concern, isSelected, onClick,
}: { concern: HealthConcern; isSelected: boolean; onClick: () => void }) {
  const sev = severityConfig[concern.severity] ?? severityConfig.unknown;
  const isMonitoring = concern.status === "monitoring";
  const sinceLabel   = isMonitoring ? "Onset Date" : "Since";

  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={cn(
        "!h-auto !w-full !whitespace-normal !rounded-xl !border-[#E5E7EB] !px-4 !py-3 !text-left !shadow-sm",
        isSelected ? "!bg-[#FFF8F8] hover:!border-[#E5E7EB] hover:!bg-[#FFF8F8]" : "!bg-white hover:!border-[#E5E7EB] hover:!bg-white hover:!shadow-md"
      )}
    >
      {/* w-full is load-bearing: antd's Button centers its content by default
          (same root cause diagnosed in DiagnosticsImagingView's sidebar rows)
          — without it, this row shrinks to its own content width and floats
          centered inside the button instead of stretching edge-to-edge. */}
      <div className="flex w-full items-start justify-between gap-3">
        {/* Left */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="break-words text-sm font-extrabold text-[#0A2947]">{concern.concern}</span>
            <span className={cn("rounded-md px-2 py-0.5 text-[11px] font-semibold", sev.bg, sev.text)}>
              {sev.label}
            </span>
          </div>
          <p className="mt-1 text-xs text-[#4B5563]">{sinceLabel} {formatDate(concern.onsetDate)}</p>
          <p className="mt-0.5 text-xs text-[#4B5563]">
            {concern.icdCode ? `ICD-10: ${concern.icdCode}` : <NotAvailable label="No ICD-10 code" />}
          </p>
        </div>
        {/* Right */}
        <div className="shrink-0 text-right">
          <p className="text-[10px] text-[#9CA3AF]">Last Updated</p>
          <p className="text-xs font-semibold text-[#0A2947]">{formatDate(concern.lastUpdated)}</p>
        </div>
      </div>
    </Button>
  );
}

// ── Right detail panel ─────────────────────────────────────────────────────
function DetailPanel({ concern, onClose }: { concern: HealthConcern; onClose: () => void }) {
  const sev = severityConfig[concern.severity] ?? severityConfig.unknown;
  const detail = {
    category: concern.icdCode ? `Condition · ICD-10 ${concern.icdCode}` : "Condition",
    since: formatDate(concern.onsetDate),
    lastUpdate: formatDate(concern.lastUpdated),
    timeline: [
      concern.onsetDate
        ? { date: formatDate(concern.onsetDate), event: "Onset recorded", detail: "Condition onset date", isLast: false }
        : null,
      {
        date: formatDate(concern.lastUpdated),
        event: "Last updated",
        detail: "Most recent change to this record",
        isLast: true,
      },
    ].filter((x): x is { date: string; event: string; detail: string; isLast: boolean } => x !== null),
  };

  return (
    <div className="rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      {/* Header */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#FFF0F3]">
            <Icon icon="lucide:heart-pulse" className="h-6 w-6 text-[#E4002B]" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-extrabold text-[#0A2947]">{concern.concern}</h2>
              <span className={cn("rounded-md px-2 py-0.5 text-[11px] font-semibold", sev.bg, sev.text)}>
                {sev.label}
              </span>
            </div>
            <p className="mt-0.5 text-xs text-[#4B5563]">Since {formatDate(concern.onsetDate)}</p>
          </div>
        </div>
        <Button variant="link" onClick={onClose} className="!h-auto !shrink-0 !p-0 !text-[#9CA3AF] hover:!text-[#0A2947]">
          <Icon icon="lucide:x" className="h-5 w-5" />
        </Button>
      </div>

      {/* About */}
      <div className="mb-4">
        <h3 className="mb-1 text-sm font-extrabold text-[#0A2947]">About this concern</h3>
        <p className="text-sm text-[#4B5563]">
          {concern.notes || <NotAvailable label="No clinical notes were included with this record." />}
        </p>
      </div>

      {/* Info chips */}
      <div className="mb-5 flex flex-wrap gap-2">
        <div className="rounded-lg border border-[#E5E7EB] px-3 py-2">
          <p className="text-[10px] text-[#9CA3AF]">Severity</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-[#0A2947]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#E4002B]" />
            {sev.label}
          </p>
        </div>
        <div className="rounded-lg border border-[#E5E7EB] px-3 py-2">
          <p className="text-[10px] text-[#9CA3AF]">Category</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-[#0A2947]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#9CA3AF]" />
            {detail.category}
          </p>
        </div>
        <div className="rounded-lg border border-[#E5E7EB] px-3 py-2">
          <p className="text-[10px] text-[#9CA3AF]">Since</p>
          <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-[#0A2947]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#9CA3AF]" />
            {detail.since}
          </p>
        </div>
        <div className="rounded-lg border border-[#E5E7EB] px-3 py-2">
          <p className="text-[10px] text-[#9CA3AF]">Last Update</p>
          <p className="mt-0.5 text-sm font-extrabold text-[#0A2947]">{detail.lastUpdate}</p>
        </div>
      </div>

      {/* Timeline */}
      <div className="mb-5">
        <h3 className="mb-3 text-sm font-extrabold text-[#0A2947]">Timeline</h3>
        <div className="relative pl-6">
          {/* Vertical line */}
          <div className="absolute left-[15px] top-1 bottom-1 w-px bg-[#E5E7EB]" />
          <div className="grid gap-5">
            {detail.timeline.map((item, index) => (
              <div key={index} className="relative flex items-start gap-4">
                {/* Dot */}
                <div className={cn(
                  "absolute -left-[15px] top-1.5 h-3 w-3 rounded-full",
                  item.isLast ? "bg-[#0A2947]" : "bg-[#E4002B]"
                )} />
                {/* Date */}
                <p className="w-24 shrink-0 text-xs text-[#4B5563]">{item.date}</p>
                {/* Content */}
                <div>
                  <p className="text-xs text-[#9CA3AF]">{item.event}</p>
                  <p className={cn(
                    "text-sm font-bold",
                    item.isLast ? "text-[#0A2947]" : "text-[#E4002B]"
                  )}>
                    {item.detail}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Care Plan */}
      <div>
        <h3 className="mb-3 text-sm font-extrabold text-[#0A2947]">Care Plan</h3>
        <EmptyState
          className="rounded-xl border-dashed py-8 shadow-none"
          icon={<Icon icon="lucide:clipboard-list" className="h-8 w-8 text-[#9CA3AF]" />}
          title="No care plan linked"
          description="See the Care & Treatment page for your care plans."
        />
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export function HealthConcernsSection({
  concerns, patientConcerns, onAddPatientConcern, isLoading = false,
}: HealthConcernsSectionProps) {
  const [activeTab, setActiveTab]             = useState<"provider" | "patient">("provider");
  const [filterKey, setFilterKey]             = useState<"all" | "active" | "monitoring" | "resolved">("all");
  const [selectedConcern, setSelectedConcern] = useState<HealthConcern | null>(null);
  const [showAddDialog, setShowAddDialog]     = useState(false);

  const [newConcern, setNewConcern] = useState({
    title: "", category: "symptom" as PatientReportedConcern["category"],
    urgency: "routine" as PatientReportedConcern["urgency"],
    description: "", duration: "", frequency: "",
    impactOnDaily: "mild" as PatientReportedConcern["impactOnDaily"],
    wantToDiscuss: true,
  });

  const activeConcerns     = concerns.filter(c => c.status === "active");
  const monitoringConcerns = concerns.filter(c => c.status === "monitoring");
  const resolvedConcerns   = concerns.filter(c => c.status === "resolved");

  // Auto-select first concern on load
  useEffect(() => {
    if (concerns.length > 0 && !selectedConcern) {
      setSelectedConcern(concerns[0]);
    }
  }, [concerns]);

  const visibleConcerns =
    filterKey === "active"     ? activeConcerns :
    filterKey === "monitoring" ? monitoringConcerns :
    filterKey === "resolved"   ? resolvedConcerns :
    concerns;

  // Group the visible list
  const visibleActive     = visibleConcerns.filter(c => c.status === "active");
  const visibleMonitoring = visibleConcerns.filter(c => c.status === "monitoring");
  const visibleResolved   = visibleConcerns.filter(c => c.status === "resolved");

  const handleAddConcern = () => {
    if (onAddPatientConcern && newConcern.title && newConcern.description) {
      onAddPatientConcern(newConcern);
      setNewConcern({ title: "", category: "symptom", urgency: "routine", description: "", duration: "", frequency: "", impactOnDaily: "mild", wantToDiscuss: true });
      setShowAddDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">
        <div className="flex items-start gap-5">
          <div className="flex-[1.4] space-y-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
                <Skeleton active paragraph={{ rows: 2 }} />
              </div>
            ))}
          </div>
          <div className="hidden flex-1 rounded-2xl border border-[#E5E7EB] bg-white p-5 shadow-sm lg:block">
            <Skeleton active paragraph={{ rows: 8 }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[#F7F7F7] px-4 py-4 lg:px-6 lg:py-5">

      {/* ── Centered pill tab switcher ── */}
      <div className="mb-5 flex justify-center">
        <Segmented
          value={activeTab}
          onChange={(v) => setActiveTab(v as "provider" | "patient")}
          options={[
            { value: "provider", label: <span className="flex items-center gap-2 px-4 py-1 text-sm font-semibold"><Icon icon="lucide:stethoscope" className="h-4 w-4" />From Providers</span> },
            { value: "patient", label: <span className="flex items-center gap-2 px-4 py-1 text-sm font-semibold"><Icon icon="lucide:user" className="h-4 w-4" />My Concerns</span> },
          ]}
          className="!rounded-full !border !border-[#D1D5DB] !bg-white !p-1 !shadow-sm [&_.ant-segmented-item]:!rounded-full [&_.ant-segmented-item-selected]:!bg-[#0A2947] [&_.ant-segmented-item-selected_span]:!text-white [&_.ant-segmented-item:not(.ant-segmented-item-selected)_span]:!text-[#9CA3AF] [&_.ant-segmented-item:not(.ant-segmented-item-selected):hover_span]:!text-[#4B5563]"
        />
      </div>


      {/* ── Provider tab ── */}
      {activeTab === "provider" && (
        <div className="flex items-start gap-5">

          {/* Left panel — 58% width */}
          <div className="w-full flex-[1.4] min-w-0">

            {/* Filter chips — all on one line */}
            <div className="mb-5 rounded-2xl border border-[#E5E7EB] bg-white p-2 shadow-sm">
              <div className="flex items-center gap-2 overflow-x-auto">
              {([
                { key: "all",        label: "All Concerns",    count: concerns.length          },
                { key: "active",     label: "Active Concerns", count: activeConcerns.length    },
                { key: "monitoring", label: "Monitoring",      count: monitoringConcerns.length },
                { key: "resolved",   label: "Resolved",        count: resolvedConcerns.length  },
              ] as const).map(chip => (
                <Button
                  key={chip.key}
                  variant="ghost"
                  onClick={() => setFilterKey(chip.key)}
                  className={cn(
                    "!flex !h-auto !shrink-0 !items-center !gap-1.5 !rounded-xl !px-3 !py-2 !text-sm !font-semibold !whitespace-nowrap",
                    filterKey === chip.key
                      ? "!bg-[#0A2947] !text-white hover:!bg-[#0A2947] hover:!text-white"
                      : "!text-[#4B5563] hover:!bg-[#F7F7F7] hover:!text-[#E4002B]"
                  )}
                >
                  {chip.label}
                  <span className={cn(
                    "flex h-5 min-w-[20px] items-center justify-center rounded px-1 text-[11px] font-bold",
                    filterKey === chip.key ? "bg-white text-[#0A2947]" : "bg-[#E4002B]/10 text-[#E4002B]/50"
                  )}>
                    {chip.count}
                  </span>
                </Button>
              ))}
              </div>
            </div>

            {/* Scrollable concern list */}
            <div className="grid gap-6">

              {visibleActive.length > 0 && (
                <div>
                  <h2 className="mb-2 text-sm font-extrabold text-[#0A2947]">Active Concerns</h2>
                  <div className="grid gap-5">
                    {visibleActive.map(c => (
                      <ConcernCard
                        key={c.id} concern={c}
                        isSelected={selectedConcern?.id === c.id}
                        onClick={() => setSelectedConcern(c)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {visibleMonitoring.length > 0 && (
                <div>
                  <h2 className="mb-2 text-sm font-extrabold text-[#0A2947]">Being Monitored</h2>
                  <div className="grid gap-5">
                    {visibleMonitoring.map(c => (
                      <ConcernCard
                        key={c.id} concern={c}
                        isSelected={selectedConcern?.id === c.id}
                        onClick={() => setSelectedConcern(c)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {visibleResolved.length > 0 && (
                <div>
                  <h2 className="mb-2 text-sm font-extrabold text-[#0A2947]">Resolved</h2>
                  <div className="grid gap-5">
                    {visibleResolved.map(c => (
                      <ConcernCard
                        key={c.id} concern={c}
                        isSelected={selectedConcern?.id === c.id}
                        onClick={() => setSelectedConcern(c)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {visibleConcerns.length === 0 && (
                <EmptyState
                  icon={<Icon icon="lucide:heart" className="h-12 w-12 text-[#9CA3AF]" />}
                  title="No concerns in this category"
                />
              )}
            </div>
          </div>

          {/* Right detail panel — 42% width. Sticky so it stays in view while
              scrolling a long concerns list, instead of scrolling away above
              the fold (matches the pattern in FhirExportView's "Export Summary"). */}
          <div className="sticky top-5 hidden flex-1 lg:block">
            {selectedConcern ? (
              <DetailPanel concern={selectedConcern} onClose={() => setSelectedConcern(null)} />
            ) : (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-[#E5E7EB] bg-white p-8 text-center shadow-sm">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FFF0F3]">
                  <Icon icon="lucide:heart-pulse" className="h-7 w-7 text-[#E4002B]" />
                </div>
                <p className="mt-3 text-sm font-semibold text-[#0A2947]">Select a concern</p>
                <p className="mt-1 text-xs text-[#9CA3AF]">Click any concern on the left to view details</p>
              </div>
            )}
          </div>

        </div>
      )}

      {/* ── Patient tab ── */}
      {activeTab === "patient" && (
        <div className="flex justify-center">
          <div className="w-full max-w-3xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-extrabold text-[#0A2947]">My Reported Concerns</h2>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="!flex !h-auto !items-center !gap-2 !rounded-lg !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#E4002B]/60"
              >
                <Icon icon="lucide:plus" className="h-4 w-4 text-white" /> Add Concern
              </Button>
            </div>

            {patientConcerns.length === 0 ? (
              <EmptyState
                icon={<Icon icon="lucide:user" className="h-12 w-12 text-[#9CA3AF]" />}
                title="No concerns reported yet"
                description="Submitting patient-reported concerns isn't available yet — this section will list concerns you raise with your provider once that's enabled."
              />
            ) : (
              <div className="grid gap-5">
                {patientConcerns.map(c => {
                  const urg = urgencyConfig[c.urgency];
                  return (
                    <div key={c.id} className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-extrabold text-[#0A2947]">{c.title}</span>
                            <span className={cn("rounded-md px-2 py-0.5 text-[11px] font-semibold", urg.bg, urg.text)}>
                              {urg.label}
                            </span>
                          </div>
                          <p className="mt-1 text-xs font-semibold text-[#E4002B]">Reported {formatDate(c.reportedDate)}</p>
                          <p className="mt-0.5 line-clamp-2 text-xs text-[#4B5563]">{c.description}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-[10px] text-[#9CA3AF]">Status</p>
                          <p className="text-xs font-semibold capitalize text-[#0A2947]">{c.status}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Add concern dialog ── */}
      <AppModal
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        title={<span className="text-[#0A2947]">Report a Health Concern</span>}
        description={<span className="text-[#4B5563]">Share a symptom, question, or concern to track or discuss with your provider.</span>}
        width={520}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAddDialog(false)} className="!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-medium !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]">
              Cancel
            </Button>
            <Button onClick={handleAddConcern} disabled={!newConcern.title || !newConcern.description} className="!flex !h-auto !items-center !gap-2 !rounded-lg !bg-[#E4002B] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!bg-[#b8001f]">
              <Icon icon="lucide:send" className="h-4 w-4" /> Submit Concern
            </Button>
          </div>
        }
      >
          <div className="flex items-start gap-2 rounded-xl border border-[#E4002B]/20 bg-[#FFF0F3] px-3 py-2.5">
            <Icon icon="lucide:alert-triangle" className="mt-0.5 h-4 w-4 shrink-0 text-[#E4002B]" />
            <p className="text-xs text-[#E4002B]">For urgent concerns, please contact your healthcare provider directly.</p>
          </div>
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">What is your concern?</label>
              <Input className="!w-full !rounded-lg !border-[#E5E7EB] !px-3 !py-2 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none" placeholder="e.g., Recurring headaches..." value={newConcern.title} onChange={e => setNewConcern({ ...newConcern, title: e.target.value })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">Category</label>
                <Select
                  className="!w-full !text-sm !text-[#0A2947]"
                  value={newConcern.category}
                  onChange={next => setNewConcern({ ...newConcern, category: next as PatientReportedConcern["category"] })}
                  options={[
                    { value: "symptom", label: "Symptom" },
                    { value: "question", label: "Question for Provider" },
                    { value: "side_effect", label: "Medication Side Effect" },
                    { value: "lifestyle", label: "Lifestyle Concern" },
                    { value: "mental_health", label: "Mental Health" },
                    { value: "other", label: "Other" },
                  ]}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">Urgency</label>
                <Select
                  className="!w-full !text-sm !text-[#0A2947]"
                  value={newConcern.urgency}
                  onChange={next => setNewConcern({ ...newConcern, urgency: next as PatientReportedConcern["urgency"] })}
                  options={[
                    { value: "routine", label: "Routine — Next visit is fine" },
                    { value: "soon", label: "Soon — Within a few days" },
                    { value: "urgent", label: "Urgent — Need attention today" },
                  ]}
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">Describe your concern</label>
              <Textarea className="!w-full !rounded-lg !border-[#E5E7EB] !px-3 !py-2 !text-sm !text-[#0A2947] placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none" placeholder="Please describe what you're experiencing..." rows={4} value={newConcern.description} onChange={e => setNewConcern({ ...newConcern, description: e.target.value })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">How long has this been happening?</label>
                <Input className="!w-full !rounded-lg !border-[#E5E7EB] !px-3 !py-2 !text-sm placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none" placeholder="e.g., 3 days, 2 weeks..." value={newConcern.duration} onChange={e => setNewConcern({ ...newConcern, duration: e.target.value })} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">How often does it occur?</label>
                <Input className="!w-full !rounded-lg !border-[#E5E7EB] !px-3 !py-2 !text-sm placeholder:!text-[#9CA3AF] focus:!border-[#E4002B]/50 focus:!shadow-none" placeholder="e.g., Daily, A few times a week..." value={newConcern.frequency} onChange={e => setNewConcern({ ...newConcern, frequency: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#0A2947]">Impact on daily activities</label>
              <Select
                className="!w-full !text-sm !text-[#0A2947]"
                value={newConcern.impactOnDaily}
                onChange={next => setNewConcern({ ...newConcern, impactOnDaily: next as PatientReportedConcern["impactOnDaily"] })}
                options={[
                  { value: "none", label: "No impact" },
                  { value: "mild", label: "Mild — Slightly affects activities" },
                  { value: "moderate", label: "Moderate — Limits some activities" },
                  { value: "severe", label: "Severe — Significantly limits activities" },
                ]}
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2">
              <Checkbox
                checked={newConcern.wantToDiscuss}
                onChange={e => setNewConcern({ ...newConcern, wantToDiscuss: e.target.checked })}
                className="[&_.ant-checkbox-inner]:!rounded [&_.ant-checkbox-inner]:!border-[#E5E7EB] [&_.ant-checkbox-checked_.ant-checkbox-inner]:!border-[#E4002B] [&_.ant-checkbox-checked_.ant-checkbox-inner]:!bg-[#E4002B]"
              />
              <span className="text-sm text-[#4B5563]">I want to discuss this at my next appointment</span>
            </label>
          </div>
      </AppModal>

    </div>
  );
}
